import { useState } from "react";
import styled from "@emotion/styled";

import { InlineSaveListButton } from "components/entities/save_components/lists/InlineSaveListButton";
import { appRoutes, BuyerContext } from "lib/appRoutes";
import { EllipsisTooltipRouterLink } from "lib/core_components/EllipsisTooltip";
import { useSavedSuppliers } from "../../lib/hooks/api/signals/useSavedSuppliers";

export function SupplierNameCell({
  supplierGuid,
  supplierName,
  buyerContext,
}: {
  supplierGuid: string;
  supplierName: string;
  /** Set on a buyer profile, where the name opens the buyer↔supplier relationship instead of the supplier profile. */
  buyerContext?: BuyerContext;
}) {
  // State is used to show the save list button on mouse entering the first cell
  const [mouseEntered, setMouseEntered] = useState(false);

  // This hook is in each SupplierName component, but because of react query it should only run once
  const { data: savedSuppliers } = useSavedSuppliers();

  const { partnerGuids, competitorGuids } = savedSuppliers || {
    partnerGuids: [],
    competitorGuids: [],
  };

  const savedListNames = [
    ...(partnerGuids.some((guid) => guid === supplierGuid) ? ["My partners"] : []),
    ...(competitorGuids.some((guid) => guid === supplierGuid) ? ["My competitors"] : []),
  ];

  return (
    <NameColumn
      onMouseEnter={() => setMouseEntered(true)}
      onMouseLeave={() => setMouseEntered(false)}
    >
      <EllipsisTooltipRouterLink
        noWrap
        fullText={supplierName}
        linkText={supplierName}
        linkProps={
          buyerContext
            ? appRoutes.buyerSupplier(buyerContext, supplierGuid)
            : appRoutes.supplierAwards(supplierGuid)
        }
      />
      <InlineSaveListButton
        entityType="supplier"
        entityIds={[supplierGuid]}
        displayType={mouseEntered ? "button" : "showSavedLists"}
        savedListNames={savedListNames}
      />
    </NameColumn>
  );
}

// minmax(0, 1fr) lets the name column shrink below its text width, so long names truncate
// instead of pushing the save button onto a second line.
const NameColumn = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  line-height: 32px;
`;
